/**
 * GlobalToast - Shows toast notifications for background operations
 *
 * Two sources, one overlay:
 * - the vault upload queue (saving to Memories)
 * - moments arriving over the WebSocket (BUG-031) — this is mounted app-wide,
 *   so it's the one place that can announce an arrival wherever you happen to
 *   be standing
 *
 * WHAT IS ALLOWED TO SIT AT THE TOP OF THE SCREEN (BUG-110, Nation's call):
 * - Anything about the send/save queues shows ONLY in the camera flow — "it
 *   should only be on in the camera". That is the home screen's camera panel
 *   plus the preview/send screens, which is where both flows put you back.
 * - The progress pills ("Saving 2 items…", "Sending 1 moment…", "Uploading…")
 *   expire on their own and re-arm if the count rises. Nothing up here
 *   persists. AND (Nation, 2026-07-31, after wave 5 made transfers instant):
 *   they only appear at all if the work OUTLIVES a grace window — "I only
 *   want something appearing if there's action." A send that lands in under
 *   a second and a half shows nothing; the pill is for weak signal, big
 *   files, being offline — the times a person would wonder.
 * - The cards that need a DECISION (sign in, retry-or-discard) do not expire —
 *   they are the only handle on content still sitting on the phone — but they
 *   stay inside the camera flow with everything else.
 * - Arrivals and toasts are untouched: they must reach you anywhere (BUG-031)
 *   and they already dismiss themselves.
 * - The Offline pill (FEAT-037, Nation's redesign) is camera-only like every
 *   other pill — his call, 2026-08-07, on seeing it in chats and the Bible
 *   side: "I don't want them anywhere except the camera." It carries the
 *   queued count and is a door to the queue sheet. The word "unsent" never
 *   appears up here: drafts have their own chip beside the shutter, and the
 *   pill that mixed the two vocabularies is what BUG-122 was made of.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationStore } from '../store/navigationStore';
import { sendEngine } from '../services/queue/engine';
import type { SendResult, SendState } from '../services/queue/types';
import { onMomentArrival, MomentArrival } from '../services/momentArrivals';
import { useToastStore } from '../store/toastStore';
import { useCameraStore, type PickProgress } from '../store/cameraStore';
import { useOnline } from '../hooks/useOnline';
import { Avatar, Spinner } from '../atoms';
import { StuckQueueCard } from '../molecules';
import SendQueueSheet from './SendQueueSheet';
import { Check, X, Upload, WifiOff, Send, LogIn } from 'lucide-react';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'pending';
  message: string;
  timestamp: number;
}

// Long enough to read and tap, short enough to stay out of the way
const ARRIVAL_DURATION = 5000;

/** How long a progress pill stays up. Long enough to be reassuring, short
 * enough that it is gone before you have finished looking at it. */
const PROGRESS_DURATION = 4000;

/** How long work must OUTLIVE before any pill appears (Nation, 2026-07-31):
 * wave 5 made transfers instant, so a pill that flashes for a sub-second
 * upload reads as a glitch. Anything that finishes inside this window shows
 * nothing; anything slower is real action and earns its pill. */
const PROGRESS_GRACE = 1500;

/**
 * False until `count` has been non-zero for PROGRESS_GRACE — instant work
 * shows nothing — then true for a few seconds, then false; armed again the
 * next time the count rises (a second save queued while the first is in
 * flight; a rise while visible extends the stay). Zero clears everything
 * immediately: work that finished has nothing to report.
 *
 * The timers live in refs on purpose: tying them to the effect's cleanup
 * meant a count DROPPING (one of three saves landing) cancelled the timer
 * without re-arming it, which is how you build a pill that never leaves.
 */
function useTransientCount(count: number, ms = PROGRESS_DURATION): boolean {
  const [visible, setVisible] = useState(false);
  const previousRef = useRef(0);
  const countRef = useRef(0);
  const visibleRef = useRef(false);
  const graceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    countRef.current = count;
    const clearGrace = () => {
      if (graceRef.current) clearTimeout(graceRef.current);
      graceRef.current = null;
    };
    const clearExpiry = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    const hide = () => {
      visibleRef.current = false;
      setVisible(false);
    };
    const showThenExpire = () => {
      visibleRef.current = true;
      setVisible(true);
      clearExpiry();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        hide();
      }, ms);
    };

    if (count === 0) {
      previousRef.current = 0;
      clearGrace();
      clearExpiry();
      hide();
      return;
    }

    if (count > previousRef.current) {
      if (visibleRef.current) {
        showThenExpire(); // already up — a rise extends the stay
      } else if (!graceRef.current) {
        // The earliest rise starts the clock; later rises ride the same grace.
        graceRef.current = setTimeout(() => {
          graceRef.current = null;
          if (countRef.current > 0) showThenExpire();
        }, PROGRESS_GRACE);
      }
    }
    previousRef.current = count;
  }, [count, ms]);

  useEffect(
    () => () => {
      if (graceRef.current) clearTimeout(graceRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return visible;
}

/**
 * What a pick in flight says. Plain words about what is happening to HIS
 * pictures — never the machinery ("transcoding", "muxing") that is doing it.
 */
function pickLabel({ done, total, mode, pct }: PickProgress): string {
  const nth = `${Math.min(done + 1, total)} of ${total}`;
  if (mode === 'split') return `Splitting… ${nth}`;
  if (mode === 'ready') {
    const percent = `${pct ?? 0}%`;
    return total > 1 ? `Getting them ready… ${nth} · ${percent}` : `Getting it ready… ${percent}`;
  }
  return total > 1 ? `Adding ${nth}…` : 'Adding…';
}

export default function GlobalToast() {
  const navigate = useNavigate();
  const location = useLocation();
  const activePanel = useNavigationStore((s) => s.activePanel);
  const [toasts, setToasts] = useState<Toast[]>([]);
  // BUG-058 (Nation's directive): the send queue gets the same always-visible
  // treatment as the Memories queue — "a little indicator, super simple".
  // One queue since FEAT-028 wave 4; sends and saves are still counted apart,
  // because "Saving 2 items" and "Sending 1 moment" are different sentences.
  const [sendState, setSendState] = useState<SendState>({
    pending: 0,
    stuck: 0,
    offline: false,
    needsAuth: false,
    saving: 0,
    savingStuck: 0,
  });
  const [arrivals, setArrivals] = useState<MomentArrival[]>([]);
  // Bytes the SERVER has confirmed, over the bytes those uploads are aiming at
  // — head starts included. Nothing here is estimated (BUG-058).
  const [upload, setUpload] = useState({ sent: 0, total: 0 });
  // FEAT-037: every pill up here is a door to the same list.
  const [queueOpen, setQueueOpen] = useState(false);
  const appToasts = useToastStore((s) => s.toasts);
  // FEAT-037 "when it empties, say so": what ended the batch, and whether a
  // pill was ever up for it. A queue emptied by Discard never congratulates,
  // and work too quick to have earned a pill stays silent at the end too.
  const lastOutcomeRef = useRef<'success' | 'other' | null>(null);
  const sawPillRef = useRef(false);
  const hadSendsRef = useRef(false);
  // High-water mark of the current batch, so a pill can say "2 of 5".
  const sendHighRef = useRef(0);
  const saveHighRef = useRef(0);
  const prevActiveRef = useRef(0);

  // Handle upload completion. A failure the queue will retry stays silent here
  // — the retry-or-discard card below is its surface. A PERMANENT refusal has
  // no card any more (the row is deleted, BUG-110), so it has to say so once,
  // wherever the user happens to be, and then get out of the way.
  const handleUploadComplete = useCallback((result: SendResult) => {
    // Every result stamps the batch's outcome — the "All sent" note fires only
    // when the LAST thing that happened before the queue emptied was a success.
    lastOutcomeRef.current = result.success ? 'success' : 'other';
    // One queue carries sends and saves; only a save has a toast to give. A
    // send's outcome is the bubble's own business.
    const dest = result.row.dest;
    if (dest.kind !== 'vault') return;
    if (!result.success && !result.permanent) return;
    const toast: Toast = {
      id: result.row.id,
      type: result.success ? 'success' : 'error',
      message: result.success
        ? dest.isPrivate
          ? 'Saved to Only Me'
          : 'Saved to Memories'
        : result.error || "Couldn't save",
      timestamp: Date.now(),
    };

    setToasts(prev => [...prev, toast]);

    // Long enough to read; a failure gets a little longer than a success
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== toast.id));
    }, result.success ? 3000 : 5000);
  }, []);

  // Subscribe to upload queue events
  useEffect(() => {
    const unsubResult = sendEngine.onResult(handleUploadComplete);
    const unsubSendState = sendEngine.onState(setSendState);
    // Its own signal, and deliberately not the state one: an offset lands
    // every couple of megabytes and must not drag a drafts recount with it.
    const unsubProgress = sendEngine.onProgress(() =>
      setUpload(sendEngine.uploadProgress())
    );

    return () => {
      unsubResult();
      unsubSendState();
      unsubProgress();
    };
  }, [handleUploadComplete]);

  // Announce incoming moments
  useEffect(() => {
    return onMomentArrival(arrival => {
      // One banner per sender — three moments in a row shouldn't stack up
      setArrivals(prev => [
        ...prev.filter(a => a.senderId !== arrival.senderId),
        arrival,
      ]);

      setTimeout(() => {
        setArrivals(prev => prev.filter(a => a.messageId !== arrival.messageId));
      }, ARRIVAL_DURATION);
    });
  }, []);

  const openConversation = useCallback(
    (arrival: MomentArrival) => {
      setArrivals(prev => prev.filter(a => a.senderId !== arrival.senderId));
      // A MOMENT lands on the chats list — the sender's card is the way he
      // opens a run, and "it opens up the individual chat" was the complaint
      // (FEAT-040). A text goes to the thread it belongs to.
      navigate(arrival.isMoment ? '/?panel=chat' : `/chat/${arrival.senderId}`);
    },
    [navigate]
  );

  // "When the queue empties: say so, don't just disappear" (FEAT-037). Runs
  // on the >0 → 0 transition of everything the pills count.
  useEffect(() => {
    const active = sendState.pending + sendState.saving;
    if (prevActiveRef.current > 0 && active === 0) {
      if (
        lastOutcomeRef.current === 'success' &&
        sawPillRef.current &&
        hadSendsRef.current
      ) {
        useToastStore.getState().show('All sent', 'success');
      }
      sawPillRef.current = false;
      hadSendsRef.current = false;
      lastOutcomeRef.current = null;
    }
    prevActiveRef.current = active;
  }, [sendState.pending, sendState.saving]);

  // "It should only be on in the camera" (Nation, BUG-110): the capture flow is
  // the home screen's camera panel plus the screens it pushes. The route half is
  // load-bearing — `activePanel` keeps saying 'camera' after you navigate away.
  const inCameraFlow =
    location.pathname.startsWith('/camera') ||
    (location.pathname === '/' && activePanel === 'camera');

  const pickProgress = useCameraStore((s) => s.pickProgress);
  const online = useOnline();
  // Offline, the progress pills stand down — the Offline pill owns that story.
  // They are fed 0 rather than merely hidden, so that reconnect is a 0→N RISE:
  // useTransientCount only arms on a rise, and a count that had silently sat
  // at 3 across the gap would never show the drain he most cares about seeing.
  const savingVisible = useTransientCount(online ? sendState.saving : 0);
  const sendingVisible = useTransientCount(online ? sendState.pending : 0);
  // Bytes actually moving — including a head start, which has no queue row and
  // so is invisible to every count above.
  const uploading = upload.total > 0 && upload.sent < upload.total;
  const uploadPercent = uploading
    ? Math.min(99, Math.floor((upload.sent / upload.total) * 100))
    : 0;
  const uploadingVisible = useTransientCount(online && uploading ? 1 : 0);
  // The send screen is where he is deciding where a moment goes; a pill about
  // that moment's own bytes on top of it is noise.
  const onSendScreen = location.pathname.startsWith('/camera/send');
  const showSaving = online && inCameraFlow && savingVisible && sendState.saving > 0;
  // Nothing about signing in is actionable offline; the Offline pill is the
  // honest surface until the connection is back.
  const showNeedsAuth = online && inCameraFlow && sendState.needsAuth;
  const showUploading =
    online && inCameraFlow && !onSendScreen && uploadingVisible && uploading && !sendState.needsAuth;
  const showSending =
    online &&
    inCameraFlow &&
    sendingVisible &&
    sendState.pending > 0 &&
    !sendState.needsAuth &&
    !showUploading;
  const showStuckSends = inCameraFlow && sendState.stuck > 0;
  const showStuckSaves = inCameraFlow && sendState.savingStuck > 0;
  // Camera-only like everything else (Nation, 2026-08-07): the pick keeps
  // RUNNING while he wanders off to a chat — only the pill stays home.
  const showPick = inCameraFlow && pickProgress !== null;
  const showOffline = inCameraFlow && !online;

  // High-water marks, kept during render on purpose: an effect lags a frame
  // behind a rise, and the first paint of a batch would say "1 of 1".
  if (sendState.pending === 0) sendHighRef.current = 0;
  else if (sendState.pending > sendHighRef.current) sendHighRef.current = sendState.pending;
  if (sendState.saving === 0) saveHighRef.current = 0;
  else if (sendState.saving > saveHighRef.current) saveHighRef.current = sendState.saving;
  if (sendState.pending > 0) hadSendsRef.current = true;
  if (sendingVisible || uploadingVisible || savingVisible) sawPillRef.current = true;

  const sendTotal = sendHighRef.current;
  const sendDone = Math.max(1, Math.min(sendTotal, sendTotal - sendState.pending + 1));
  const saveTotal = saveHighRef.current;
  const saveDone = Math.max(1, Math.min(saveTotal, saveTotal - sendState.saving + 1));

  // Every row the sheet would list — sends, saves, and the stuck of both. The
  // Offline pill is a door to that sheet; a count that disagreed with what the
  // sheet shows would be a new lie.
  const queuedCount =
    sendState.pending + sendState.stuck + sendState.saving + sendState.savingStuck;

  // The viewfinder keeps its top controls (profile, camera roll, flash) at
  // safe-top + 1rem — the same offset this rail used to start at, which is how
  // a long pill sat exactly on the camera-roll button (FEAT-037).
  const onViewfinder = location.pathname === '/' && activePanel === 'camera';

  const queueSheet = (
    <SendQueueSheet open={queueOpen} onClose={() => setQueueOpen(false)} />
  );

  // Don't render if nothing to show.
  if (
    !showOffline &&
    toasts.length === 0 &&
    appToasts.length === 0 &&
    arrivals.length === 0 &&
    !showSaving &&
    !showSending &&
    !showUploading &&
    !showNeedsAuth &&
    !showStuckSends &&
    !showStuckSaves &&
    !showPick
  ) {
    return queueSheet;
  }

  return (
    <>
    <div
      className="fixed inset-x-0 top-0 z-[9999] flex flex-col items-center gap-2 px-4 pt-4 pointer-events-none"
      style={{
        // On the viewfinder the rail starts BELOW the camera's top controls
        // (safe-top + 1rem padding + 2.5rem buttons + breathing room), so no
        // pill can ever sit on the camera-roll button again (FEAT-037).
        paddingTop: onViewfinder
          ? 'calc(4.25rem + var(--sa-top))'
          : 'calc(1rem + var(--sa-top))',
      }}
    >
      {/* THE offline surface (FEAT-037, Nation's redesign): one pill, the word
          "Offline", the number of things waiting, and it opens the queue like
          every other pill. Camera-only, like every other pill. */}
      {showOffline && (
        <button
          onClick={() => setQueueOpen(true)}
          aria-label="Offline — see what's waiting to send"
          className="glass max-w-sm px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto animate-fade-in active:scale-95"
        >
          <WifiOff size={16} className="text-text-muted" />
          <span className="truncate text-sm font-medium text-text">
            {queuedCount > 0 ? `Offline · ${queuedCount} waiting` : 'Offline'}
          </span>
        </button>
      )}

      {/* Incoming moments - tapping opens the conversation */}
      {arrivals.map(arrival => (
        <button
          key={arrival.messageId}
          onClick={() => openConversation(arrival)}
          // Opaque on purpose: this lands over the dark header band, and a
          // translucent card there is unreadable
          className="w-full max-w-sm flex items-center gap-3 rounded-2xl border border-divider bg-surface px-3 py-2.5 text-left shadow-xl pointer-events-auto animate-slide-down transition-transform active:scale-95"
        >
          <Avatar src={arrival.avatarUrl || undefined} name={arrival.senderName} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-text">
              {arrival.senderName}
            </span>
            <span className="block truncate text-xs text-text-muted">
              sent you a {arrival.isMoment ? 'moment' : 'message'}
            </span>
          </span>
        </button>
      ))}

      {/* Saves in flight. Camera-only, and it expires: the work carries on in
          the background either way. Offline it stands down — the Offline pill
          above carries the count. */}
      {showSaving && (
        <button
          onClick={() => setQueueOpen(true)}
          className="glass max-w-sm px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto animate-fade-in active:scale-95"
        >
          <Upload size={16} className="text-primary" />
          <span className="truncate text-sm font-medium text-text">
            {saveTotal > 1
              ? `Saving ${saveDone} of ${saveTotal}…`
              : `Saving ${sendState.saving} ${sendState.saving === 1 ? 'item' : 'items'}...`}
          </span>
        </button>
      )}

      {/* Bytes on the wire (FEAT-038 wave 3). The number is the server's own
          committed offset — a moment recorded but not yet addressed counts
          here too, because its head start is genuinely uploading. */}
      {showUploading && (
        <button
          onClick={() => setQueueOpen(true)}
          className="glass max-w-sm px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto animate-fade-in active:scale-95"
        >
          <Upload size={16} className="text-primary" />
          <span className="truncate text-sm font-medium text-text">
            {sendTotal > 1
              ? `Sending ${sendDone} of ${sendTotal} · ${uploadPercent}%`
              : `Uploading… ${uploadPercent}%`}
          </span>
        </button>
      )}

      {/* A camera-roll pick being prepared (FEAT-032/034). Up here with the
          rest of them so the viewfinder underneath stays live — he can keep
          shooting while four videos re-encode in the background. */}
      {showPick && pickProgress && (
        <div className="glass px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto animate-fade-in">
          <Spinner size="sm" />
          <span className="text-sm font-medium text-text">
            {pickLabel(pickProgress)}
          </span>
        </div>
      )}

      {/* Signed out with moments still queued (BUG-069): they are safe and
          waiting, not failed — one tap goes and finishes the job */}
      {showNeedsAuth && (
        <button
          onClick={() => navigate('/login')}
          className="glass px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto animate-fade-in active:scale-95"
        >
          <LogIn size={16} className="text-primary" />
          <span className="text-sm font-medium text-text">
            Sign in to finish sending {sendState.pending}{' '}
            {sendState.pending === 1 ? 'moment' : 'moments'}
          </span>
        </button>
      )}

      {/* Sending pill (BUG-058): anything leaving the phone is visible when it
          starts. It used to stay until the last byte landed, on every screen —
          BUG-110 scoped it to the camera and gave it an ending. */}
      {showSending && (
        <button
          onClick={() => setQueueOpen(true)}
          className="glass max-w-sm px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto animate-fade-in active:scale-95"
        >
          <Send size={16} className="text-primary" />
          <span className="truncate text-sm font-medium text-text">
            {sendTotal > 1
              ? `Sending ${sendDone} of ${sendTotal}…`
              : `Sending ${sendState.pending} ${sendState.pending === 1 ? 'moment' : 'moments'}...`}
          </span>
        </button>
      )}

      {/* Stuck sends — kept, never silently discarded; the user decides.
          Discard deletes the queue rows only; drafts survive. No timeout: this
          one needs an answer. Camera-only, like everything else up here. */}
      {showStuckSends && (
        <StuckQueueCard
          count={sendState.stuck}
          noun="moment"
          verb="send"
          onRetry={() => void sendEngine.retryStuck('send')}
          onDiscard={() => void sendEngine.discardStuck('send')}
          onDetails={() => setQueueOpen(true)}
        />
      )}

      {/* Stuck uploads — kept, never silently discarded; the user decides. A
          save the server REFUSED no longer lands here at all: it is dropped and
          announced once (BUG-110), so this card means "worth another try". */}
      {showStuckSaves && (
        <StuckQueueCard
          count={sendState.savingStuck}
          noun="item"
          verb="save"
          onRetry={() => void sendEngine.retryStuck('vault')}
          onDiscard={() => void sendEngine.discardStuck('vault')}
          onDetails={() => setQueueOpen(true)}
        />
      )}

      {/* Imperative toasts from anywhere in the app (toastStore) */}
      {appToasts.map(toast => (
        <div
          key={`app-${toast.id}`}
          className={`px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto animate-slide-down ${
            toast.type === 'success'
              ? 'bg-success text-on-primary'
              : 'bg-error text-on-primary'
          }`}
        >
          {toast.type === 'success' ? <Check size={18} /> : <X size={18} />}
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      ))}

      {/* Toast notifications */}
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`
            px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2
            pointer-events-auto animate-slide-down
            ${toast.type === 'success'
              ? 'bg-success text-on-primary'
              : 'bg-error text-on-primary'
            }
          `}
        >
          {toast.type === 'success' ? (
            <Check size={18} />
          ) : (
            <X size={18} />
          )}
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      ))}
    </div>
    {queueSheet}
    </>
  );
}
